import { useDispatch, useSelector } from "react-redux";
import {
    Typography,
    TextField,
    FormControlLabel,
    RadioGroup,
    Radio,
    Checkbox,
    Box,
    Select,
    MenuItem,
    FormControl,
    FormHelperText, Stack
} from "@mui/material";
import { useState, useEffect } from "react";
import SnackbarMessage from "../../components/snackBar/snackBar";
import { addFormResponseThunk } from "../form/formsThunk";
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import TabContext from '@mui/lab/TabContext';
import TabPanel from '@mui/lab/TabPanel';
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { FieldTextField } from "../../components/selections/fieldInputs";
import { FIELD } from "../../components/selections/fieldTheme";
import { QR, QrActionButton } from "../qrCode/components/qrUi";

// Same look as the shared field kit (label above, rounded 42px box) for controls the kit can't replace
const fieldLabelSx = {
    display: "block",
    mb: 0.9,
    fontSize: 13,
    fontWeight: 500,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: "#4b5563",
};

const fieldBoxSx = (error) => ({
    "& .MuiOutlinedInput-root": {
        minHeight: 42,
        borderRadius: "10px",
        bgcolor: "#fff",
        fontSize: 14,
        color: "#111827",
        transition: "box-shadow .15s ease",
        "& .MuiOutlinedInput-notchedOutline": { borderWidth: "1.5px", borderColor: error ? "#ef4444" : "#e5e7eb" },
        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: error ? "#ef4444" : "#cbd5e1" },
        "&.Mui-focused": { boxShadow: `0 0 0 4px ${error ? "rgba(239,68,68,0.12)" : FIELD.focusRing}` },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderWidth: "1.5px", borderColor: error ? "#ef4444" : FIELD.accent },
        "&.Mui-disabled": { bgcolor: "#f8fafc" },
        "&.Mui-disabled .MuiOutlinedInput-notchedOutline": { borderColor: "#eef2f6" },
        "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: "#64748b" },
    },
    "& .MuiOutlinedInput-input": { py: "9.5px" },
    "& .MuiFormHelperText-root": { mx: 0.25, mt: 0.5 },
});

const selectMenuProps = {
    PaperProps: {
        sx: {
            mt: 0.75,
            borderRadius: 2.5,
            border: "1px solid #e5e7eb",
            boxShadow: "0 16px 32px -16px rgba(15,23,42,0.3)",
            "& .MuiList-root": { p: 0.5 },
            "& .MuiMenuItem-root": {
                borderRadius: 2,
                fontSize: 13.5,
                minHeight: 34,
                color: "#334155",
                "&.Mui-selected": { bgcolor: FIELD.selectedBg, color: FIELD.selectedText, fontWeight: 600 },
            },
        },
    },
};

const checkSx = { color: "#cbd5e1", "&.Mui-checked": { color: FIELD.accent } };

const FieldLabel = ({ label, required }) => (
    <Typography component="label" sx={fieldLabelSx}>
        {label}
        {required && (
            <Box component="span" sx={{ color: "#ef4444", ml: 0.5 }}>
                *
            </Box>
        )}
    </Typography>
);

// Long fields are shown after the short ones
const isLongField = (type) => (type === "textarea" || type === "radio" ? 1 : 0);

export default function UserForms1({ handleClose, show, contact_id }) {
    const { getUserProfile } = useSelector((state) => state.contacts);
    const { formsdata } = getUserProfile;

    const dispatch = useDispatch();
    const { status } = useSelector((state) => state.organizatiomForm);
    const { selectedUser } = useSelector((state) => state.messages);

    const [formFieldData, setFormFieldData] = useState([]);
    const [errors, setErrors] = useState({});
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const [value, setValue] = useState("0");
    const [changedFields, setChangedFields] = useState(new Set());
    const isEdit = true; // form fields are always editable (no Edit step)

    const handleChange = (event, newValue) => {
        setValue(newValue);
        setErrors({});
        setChangedFields((prev) => {
            if (prev.size > 0) {
                setFormFieldData(JSON.parse(JSON.stringify(formsdata)));
                return new Set()
            }
            return prev;
        });
    };

    useEffect(() => {
        if (formsdata && show) {
            setFormFieldData(JSON.parse(JSON.stringify(formsdata)));
            setChangedFields(new Set());
        }
    }, [formsdata, show]);


    const handleValueChange = (formIndex, fieldIndex, value) => {
        setFormFieldData((prev) => {
            const updated = [...prev];
            updated[formIndex] = { ...updated[formIndex] };
            updated[formIndex].fields = [...updated[formIndex].fields];
            updated[formIndex].fields[fieldIndex] = {
                ...updated[formIndex].fields[fieldIndex],
                value,
            };
            return updated;
        });

        setChangedFields((prev) => {
            const updated = new Set(prev);
            updated.add(`${formIndex}_${fieldIndex}`);
            return updated;
        });


        setErrors((prev) => ({
            ...prev,
            [`${formIndex}_${fieldIndex}`]: "",
        }));
    };

    // Handle multi-value inputs (checkbox / multi-select)
    const handleMultiValueChange = (formIndex, fieldIndex, newValues) => {
        setFormFieldData((prev) => {
            const updated = [...prev];
            updated[formIndex] = { ...updated[formIndex] };
            updated[formIndex].fields = [...updated[formIndex].fields];
            updated[formIndex].fields[fieldIndex] = {
                ...updated[formIndex].fields[fieldIndex],
                value: newValues,
            };
            return updated;
        });

        setChangedFields((prev) => {
            const updated = new Set(prev);
            updated.add(`${formIndex}_${fieldIndex}`);
            return updated;
        });

        setErrors((prev) => ({
            ...prev,
            [`${formIndex}_${fieldIndex}`]: "",
        }));
    };

    // Validate required fields and submit form
    const handleSubmitForm = async (formIndex) => {
        const form = formFieldData[formIndex];
        let newErrors = {};
        let hasError = false;

        form.fields.forEach((f, fieldIndex) => {
            const val = f.value;
            if (f.is_required === 1 && (!val || (Array.isArray(val) && val.length === 0))) {
                newErrors[`${formIndex}_${fieldIndex}`] = `${f.field_name} is required`;
                hasError = true;
            }
        });

        if (hasError) {
            setErrors(newErrors);
            setSnackbar({
                open: true,
                message: "Please fill all required fields",
                severity: "error",
            });
            return;
        }

        try {
            const result = await dispatch(
                addFormResponseThunk({
                    credentials: {
                        wa_contact_id: contact_id ?? selectedUser,
                        form_id: form?.form_id ?? "",
                        responses: form?.fields.map((item) => ({ value: item.value, field_id: item.field_id }))
                    }
                })
            );

            if (result.meta.requestStatus === "fulfilled") {
                setSnackbar({
                    open: true,
                    message: result.payload.message,
                    severity: "success",
                });
                handleClose();
            } else {
                setSnackbar({
                    open: true,
                    message: result.payload,
                    severity: "error",
                });
            }
        } catch {
            setSnackbar({
                open: true,
                message: "Server error. Please try again.",
                severity: "error",
            });
        }
    };

    return (
        <div>
            {formFieldData.length > 0 ? (
                <Box sx={{ width: "100%", borderRadius: 3, border: `1px solid ${QR.border}`, bgcolor: "#fff", overflow: "hidden", boxShadow: "0 1px 3px rgba(16,24,40,0.05)" }}>
                    <TabContext value={value}>
                        {/* Card header: form tabs (name + field count) and Edit on the right */}
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ pr: 1.25, bgcolor: "#f8fafc", borderBottom: `1px solid ${QR.border}` }}>
                        <Tabs
                            value={value}
                            onChange={handleChange}
                            aria-label="form tabs"
                            variant="scrollable"
                            scrollButtons="auto"
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                minHeight: 52,
                                px: 0.75,
                                "& .MuiTabs-flexContainer": { alignItems: "center", height: 52 },
                                "& .MuiTabs-indicator": { display: "none" },
                                "& .MuiTabs-scrollButtons": { width: 28, borderRadius: 2 },
                            }}
                        >
                            {formFieldData.map((form, formIndex) => (
                                <Tab
                                    key={formIndex}
                                    label={
                                        <Stack direction="row" alignItems="center" spacing={0.75}>
                                            <span>{form.form_name}</span>
                                            <Box component="span" className="tab-count" sx={{ minWidth: 20, height: 18, px: 0.6, borderRadius: 5, fontSize: 11, fontWeight: 800, lineHeight: "18px", textAlign: "center", bgcolor: "#e2e8f0", color: "#475569" }}>
                                                {form.fields.length}
                                            </Box>
                                        </Stack>
                                    }
                                    value={formIndex.toString()}
                                    icon={<DescriptionOutlinedIcon sx={{ fontSize: 17 }} />}
                                    iconPosition="start"
                                    sx={{
                                        minHeight: 36,
                                        py: 0.75,
                                        px: 1.75,
                                        mr: 0.5,
                                        borderRadius: 2.5,
                                        textTransform: "none",
                                        fontSize: 14,
                                        fontWeight: 600,
                                        color: "#64748b",
                                        transition: "all .15s ease",
                                        "&:hover": { color: QR.main, bgcolor: "#fff" },
                                        "&.Mui-selected": {
                                            color: "#fff",
                                            background: `linear-gradient(135deg, ${QR.main}, ${QR.light})`,
                                            boxShadow: `0 6px 14px -8px ${QR.main}`,
                                            "& .tab-count": { bgcolor: "rgba(255,255,255,0.25)", color: "#fff" },
                                        },
                                    }}
                                />
                            ))}
                        </Tabs>
                        </Stack>

                        {/* Tabs Content */}
                        {formFieldData.map((form, formIndex) => (
                            <TabPanel key={formIndex} value={formIndex.toString()} sx={{ p: 0 }}>
                                {/* Grid: short fields fill 3 columns (dense packing fills gaps), long fields (textarea, radio) take a full row */}
                                <Box sx={{ p: { xs: 1.75, sm: 2.25 }, display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))", md: "repeat(3, minmax(0, 1fr))" }, gridAutoFlow: "row dense", columnGap: 2.5, rowGap: 2.5 }}>
                                    {/* short fields first, then long ones (textarea, radio); fieldIndex stays the original position */}
                                    {form.fields
                                        .map((field, fieldIndex) => ({ field, fieldIndex }))
                                        .sort((a, b) => isLongField(a.field.field_type) - isLongField(b.field.field_type))
                                        .map(({ field, fieldIndex }) => {
                                        const { field_type, field_name, options, value, value_length } = field;
                                        const fieldError = errors[`${formIndex}_${fieldIndex}`];
                                        const isRequired = field.is_required !== 0;
                                        const isWide = field_type === "radio"; // textarea keeps the normal column width
                                        const usesKitLabel = field_type === "text" || field_type === "textarea";

                                        return (
                                            <Box key={field.field_id} sx={{ gridColumn: isWide ? "1 / -1" : "auto", minWidth: 0 }}>
                                                {!usesKitLabel && <FieldLabel label={field_name} required={isRequired} />}

                                                {/* TEXT */}
                                                {field_type === "text" && (
                                                    <FieldTextField
                                                        name={`form_${formIndex}_field_${field.field_id}`}
                                                        label={field_name}
                                                        required={isRequired}
                                                        value={value}
                                                        maxLength={value_length || 100}
                                                        onChange={(e) =>
                                                            handleValueChange(formIndex, fieldIndex, e.target.value)
                                                        }
                                                        error={fieldError}
                                                        disabled={!isEdit}
                                                    />
                                                )}

                                                {/* TEXTAREA */}
                                                {field_type === "textarea" && (
                                                    <FieldTextField
                                                        name={`form_${formIndex}_field_${field.field_id}`}
                                                        label={field_name}
                                                        required={isRequired}
                                                        multiline
                                                        rows={3}
                                                        value={value}
                                                        maxLength={value_length || 500}
                                                        onChange={(e) =>
                                                            handleValueChange(formIndex, fieldIndex, e.target.value)
                                                        }
                                                        error={fieldError}
                                                        disabled={!isEdit}
                                                    />
                                                )}


                                                {/* NUMBER */}
                                                {field_type === "number" && (
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        type="text"
                                                        inputProps={{
                                                            inputMode: "numeric",
                                                            pattern: "[0-9]*",
                                                            maxLength: value_length ?? 10
                                                        }}
                                                        value={value || ""}
                                                        onChange={(e) => {
                                                            let val = e.target.value.replace(/\D/g, "");
                                                            if (val.length > (value_length ?? 10)) val = val.slice(0, value_length ?? 10);
                                                            handleValueChange(formIndex, fieldIndex, val);
                                                        }}
                                                        error={!!fieldError}
                                                        disabled={!isEdit}
                                                        sx={fieldBoxSx(fieldError)}
                                                    />
                                                )}


                                                {/* DATE */}
                                                {field_type === "date" && (
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        type="date"
                                                        InputLabelProps={{ shrink: true }}
                                                        value={value || ""}
                                                        error={!!fieldError}
                                                        onChange={(e) =>
                                                            handleValueChange(formIndex, fieldIndex, e.target.value)
                                                        }
                                                        disabled={!isEdit}
                                                        sx={fieldBoxSx(fieldError)}
                                                    />
                                                )}

                                                {/* RADIO */}
                                                {field_type === "radio" && (
                                                    <RadioGroup
                                                        row
                                                        value={value || ""}
                                                        onChange={(e) =>
                                                            handleValueChange(formIndex, fieldIndex, e.target.value)
                                                        }
                                                        sx={{
                                                            gap: 1,
                                                            px: 1.25,
                                                            py: 0.5,
                                                            minHeight: 42,
                                                            borderRadius: "10px",
                                                            border: `1.5px solid ${fieldError ? "#ef4444" : "#e5e7eb"}`,
                                                            bgcolor: isEdit ? "#fff" : "#f8fafc",
                                                        }}
                                                    >
                                                        {options.map((opt) => (
                                                            <FormControlLabel
                                                                key={opt.value}
                                                                value={opt.value}
                                                                control={<Radio size="small" sx={checkSx} />}
                                                                label={opt.label}
                                                                disabled={!isEdit}
                                                                sx={{ mr: 1.5, "& .MuiFormControlLabel-label": { fontSize: 14, color: "#334155" } }}
                                                            />
                                                        ))}
                                                    </RadioGroup>
                                                )}

                                                {/* SINGLE DROPDOWN */}
                                                {field_type === "Dropdown-Single" && (
                                                    <FormControl fullWidth size="small" error={!!fieldError} sx={fieldBoxSx(fieldError)}>
                                                        <Select
                                                            value={value || ""}
                                                            displayEmpty
                                                            onChange={(e) =>
                                                                handleValueChange(formIndex, fieldIndex, e.target.value)
                                                            }
                                                            disabled={!isEdit}
                                                            MenuProps={selectMenuProps}
                                                            renderValue={(selected) =>
                                                                selected === "" ? (
                                                                    <Box component="span" sx={{ color: "#9ca3af" }}>Select {field_name}</Box>
                                                                ) : (
                                                                    options.find((opt) => opt.value === selected)?.label ?? ""
                                                                )
                                                            }
                                                        >
                                                            {options.map((opt) => (
                                                                <MenuItem key={opt.value} value={opt.value}>
                                                                    {opt.label}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                        {fieldError && <FormHelperText>{fieldError}</FormHelperText>}
                                                    </FormControl>
                                                )}

                                                {/* MULTI DROPDOWN */}
                                                {field_type === "Dropdown-Multi" && (
                                                    <FormControl fullWidth size="small" error={!!fieldError} sx={fieldBoxSx(fieldError)}>
                                                        <Select
                                                            multiple
                                                            displayEmpty
                                                            value={Array.isArray(value) ? value : []}
                                                            onChange={(e) =>
                                                                handleMultiValueChange(formIndex, fieldIndex, e.target.value)
                                                            }
                                                            disabled={!isEdit}
                                                            MenuProps={selectMenuProps}
                                                            renderValue={(selected) =>
                                                                selected.length === 0 ? (
                                                                    <Box component="span" sx={{ color: "#9ca3af" }}>Select {field_name}</Box>
                                                                ) : (
                                                                    // count instead of the selected names; the menu checkboxes show which ones
                                                                    <Box
                                                                        component="span"
                                                                        title={options.filter((opt) => selected.includes(opt.value)).map((opt) => opt.label).join(", ")}
                                                                        sx={{ display: "inline-flex", alignItems: "center", height: 24, px: 1, borderRadius: 1.5, fontSize: 13, fontWeight: 700, bgcolor: isEdit ? "#eff6ff" : "#eef2f6", color: isEdit ? "#1e3a8a" : "#64748b" }}
                                                                    >
                                                                        {selected.length === options.length && options.length > 1 ? `All (${selected.length})` : `${selected.length} selected`}
                                                                    </Box>
                                                                )
                                                            }
                                                        >
                                                            {options.map((opt) => (
                                                                <MenuItem key={opt.value} value={opt.value}>
                                                                    <Checkbox
                                                                        size="small"
                                                                        sx={{ ...checkSx, p: 0.5, mr: 1 }}
                                                                        checked={
                                                                            Array.isArray(value)
                                                                                ? value.includes(opt.value)
                                                                                : false
                                                                        }
                                                                    />
                                                                    {opt.label}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                        {fieldError && <FormHelperText>{fieldError}</FormHelperText>}
                                                    </FormControl>
                                                )}
                                            </Box>
                                        );
                                    })}
                                </Box>

                                {/* Submit sits inside the card (only while editing; Edit is in the header) */}
                                {isEdit !== false && (
                                    <Stack
                                        direction="row"
                                        justifyContent="flex-end"
                                        sx={{ px: { xs: 1.75, sm: 2.25 }, py: 1.5, borderTop: `1px solid ${QR.border}`, bgcolor: "#fafbfc", "& .MuiButton-root": { height: 40 } }}
                                    >
                                        <QrActionButton
                                            variant="solid"
                                            color={QR.main}
                                            gradientTo={QR.light}
                                            icon={<TaskAltRoundedIcon sx={{ fontSize: 17 }} />}
                                            loading={status === "loading"}
                                            onClick={() => handleSubmitForm(formIndex)}
                                            label="Submit"
                                            loadingLabel="Submitting.."
                                        />
                                    </Stack>
                                )}
                            </TabPanel>
                        ))}
                    </TabContext>
                </Box>
            ) : (
                <Stack alignItems="center" justifyContent="center" spacing={1.25} sx={{ py: 6, textAlign: "center" }}>
                    <Box
                        sx={{
                            width: 56,
                            height: 56,
                            borderRadius: 3,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: QR.main,
                            bgcolor: `${QR.main}14`,
                        }}
                    >
                        <DescriptionOutlinedIcon sx={{ fontSize: 28 }} />
                    </Box>
                    <Typography sx={{ fontSize: 15.5, fontWeight: 700, color: "#334155" }}>
                        No Data Found
                    </Typography>
                    <Typography sx={{ fontSize: 13.5, color: "#94a3b8" }}>
                        There are no forms available for this contact yet.
                    </Typography>
                </Stack>
            )}

            <SnackbarMessage
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            />
        </div>
    );
};
